/**
 * Human-readable message for a Firebase Storage failure.
 *
 * Why this exists: `StorageError` sets `.status` (the HTTP status) and
 * `.serverResponse`, but `sharedErrorHandler` only maps 401/402/403 to named
 * codes — everything else, including a 404, arrives as the useless
 * `storage/unknown` "please check the error payload for server response". The
 * payload is right there on the error object; surfacing it turns a dead end into
 * a diagnosis.
 */
export function storageErrorMessage(err: unknown): string {
  if (!(err instanceof Error)) return String(err);

  const e = err as Error & {
    code?: string;
    status?: number;
    serverResponse?: string | null;
  };

  const parts = [e.message];
  if (e.status) parts.push(`HTTP ${e.status}`);
  if (e.serverResponse) parts.push(String(e.serverResponse).slice(0, 300));

  if (e.code === "storage/unauthorized") {
    parts.push("Storage rules rejected the write for this signed-in user.");
  } else if (e.status === 404) {
    // The overwrite is aimed by gs:// URI, so a 404 means the bucket itself
    // isn't reachable — usually a build pointed at the wrong Firebase project.
    parts.push("Bucket or object not found — check which Firebase project this build targets.");
  }

  return parts.join(" · ");
}
